import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Seo, breadcrumbLd } from "../lib/seo";
import { MaskedLine, FadeIn } from "../components/Reveal";
import { Eyebrow } from "../components/atoms";
import { SocialLinks } from "../components/SocialLinks";
import {
  CONTACT_OPTIONS,
  CONTACT_EMAIL,
  CONTACT_PHONE_DISPLAY,
  CONTACT_PHONE_TEL,
  CALENDLY_URL,
  CORPORATE,
} from "../data/content";
import { api } from "../lib/api";
import { toast } from "sonner";

const inputCls =
  "w-full bg-transparent border border-charcoal/25 px-4 py-3.5 text-sm text-charcoal placeholder:text-charcoal/40 outline-none transition-colors duration-300 focus:border-gold";
const labelCls = "block text-[11px] font-semibold tracking-[0.2em] uppercase text-charcoal/60 mb-2";

export default function Contact() {
  const [params] = useSearchParams();
  const initialService = CONTACT_OPTIONS.some((o) => o.key === params.get("service"))
    ? params.get("service")
    : "unsure";
  const [service, setService] = useState(initialService);
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", mobile: "", message: "" });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact", {
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        email: form.email.trim(),
        mobile: form.mobile.trim() || null,
        service,
        message: form.message.trim(),
      });
      setDone(true);
    } catch (err) {
      toast.error(err.response?.data?.detail?.[0]?.msg || "Please complete all required fields.");
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <Seo
        title="Book a call or send an enquiry — Contact | 80 Metrics"
        description="Book a 15-minute introductory call with Matthew Clark, or tell us what you’re trying to decide in writing — we’ll point you to the right starting point."
        jsonLd={[breadcrumbLd([{ name: "Home", path: "/" }, { name: "Contact", path: "/contact" }])]}
      />
      <section className="bg-charcoal text-ivory" data-testid="contact-hero">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-16 lg:pt-24 pb-12 lg:pb-16">
          <FadeIn delay={0.1}>
            <Eyebrow dark>Contact</Eyebrow>
          </FadeIn>
          <h1 className="mt-8 font-serif font-medium text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight max-w-4xl">
            <MaskedLine delay={0.25}>Talk it through, or write it down.</MaskedLine>
          </h1>
          <FadeIn delay={0.7}>
            <p className="mt-6 text-sm lg:text-base leading-relaxed text-ivory/60 max-w-xl">
              Two easy routes — pick whichever suits you. We’ll recommend the service that fits, or tell you if you don’t need one.
            </p>
          </FadeIn>
        </div>
      </section>

      <section className="bg-white border-b border-charcoal/10" data-testid="contact-routes">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-14 lg:py-20 grid grid-cols-1 lg:grid-cols-2 gap-px bg-charcoal/15 border border-charcoal/15">
          <div className="bg-ivory p-8 lg:p-12" data-testid="contact-booking">
            <Eyebrow>Fastest</Eyebrow>
            <h2 className="mt-4 font-serif text-2xl lg:text-3xl font-medium text-charcoal leading-snug">
              Book a 15-minute introductory call
            </h2>
            <p className="mt-4 text-sm text-charcoal/65 leading-relaxed max-w-md">
              A short call with Matthew to identify the appropriate next step. Scope and fees are agreed before any paid work begins.
            </p>
            <a
              href={CALENDLY_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-booking-link"
              className="mt-7 inline-flex items-center justify-center px-8 py-4 bg-gold text-ink text-xs font-semibold tracking-[0.2em] uppercase transition-[background-color] duration-300 hover:bg-ivory border border-gold"
            >
              Book my 15-minute call ↗
            </a>
          </div>
          <div className="bg-ivory p-8 lg:p-12" data-testid="contact-direct">
            <Eyebrow>Directly</Eyebrow>
            <h2 className="mt-4 font-serif text-2xl lg:text-3xl font-medium text-charcoal leading-snug">Call or email us</h2>
            <a
              href={`tel:${CONTACT_PHONE_TEL}`}
              data-testid="contact-phone-link"
              className="mt-5 block font-serif text-xl lg:text-2xl text-charcoal hover:text-golddark transition-colors duration-300"
            >
              {CONTACT_PHONE_DISPLAY}
            </a>
            <a
              href={`mailto:${CONTACT_EMAIL}`}
              data-testid="contact-email-link"
              className="mt-2 block font-serif text-xl lg:text-2xl text-charcoal hover:text-golddark transition-colors duration-300"
            >
              {CONTACT_EMAIL}
            </a>
            <p className="mt-6 text-xs text-charcoal/50 leading-relaxed" data-testid="contact-corporate">
              {CORPORATE.trading_line}
              <br />
              {CORPORATE.abn} · {CORPORATE.licence}
              <br />
              {CORPORATE.address}
            </p>
            <SocialLinks className="mt-6" testidPrefix="contact-social" />
          </div>
        </div>
      </section>

      <section className="bg-white" data-testid="contact-form-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-24 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-5">
            <h2 className="font-serif text-2xl lg:text-3xl font-medium text-charcoal">Prefer to write it down?</h2>
            <p className="mt-3 text-sm text-charcoal/60 leading-relaxed">
              Tell us what you’re trying to decide — the form is optional, not a gate.
            </p>
            <div className="mt-8 border-t border-charcoal/15" data-testid="service-options">
              {CONTACT_OPTIONS.map((o) => (
                <button
                  key={o.key}
                  onClick={() => setService(o.key)}
                  data-testid={`service-option-${o.key}`}
                  className={`w-full text-left py-5 border-b border-charcoal/15 transition-colors duration-300 group ${
                    service === o.key ? "" : "hover:bg-bone"
                  }`}
                >
                  <span className="flex items-baseline justify-between gap-4">
                    <span className={`font-serif text-lg lg:text-xl font-medium ${service === o.key ? "text-golddark" : "text-charcoal"}`}>
                      {o.label}
                    </span>
                    {o.service && (
                      <span className={`text-[10px] font-semibold tracking-[0.2em] uppercase whitespace-nowrap ${service === o.key ? "text-golddark" : "text-charcoal/40"}`}>
                        {o.service}
                      </span>
                    )}
                  </span>
                </button>
              ))}
            </div>
            {service === "suburbiq" && (
              <p className="mt-6 border border-gold/50 bg-bone px-5 py-4 text-xs leading-relaxed text-charcoal/75" data-testid="suburbiq-hint">
                SuburbIQ is ordered and paid online —{" "}
                <Link to="/suburbiq" className="font-semibold text-golddark underline underline-offset-4">
                  start your SuburbIQ order →
                </Link>{" "}
                or send this form and we’ll guide you.
              </p>
            )}
            {service === "unsure" && (
              <p className="mt-6 border border-gold/50 bg-bone px-5 py-4 text-xs leading-relaxed text-charcoal/75" data-testid="unsure-hint">
                Tell us what you’re trying to decide. We’ll point you to the right starting point — including telling you if you don’t need a paid service.
              </p>
            )}
          </div>

          <div className="lg:col-span-7">
            {done ? (
              <div className="border border-charcoal/15 bg-bone p-10 lg:p-14" data-testid="contact-success">
                <p className="text-xs font-semibold tracking-[0.28em] uppercase text-golddark">Received</p>
                <h3 className="mt-4 font-serif text-3xl font-medium text-charcoal">Thank you — we’ve got it.</h3>
                <p className="mt-4 text-sm text-charcoal/65 leading-relaxed max-w-md">
                  We’ll come back to you with the right starting point — including telling you if you don’t need a paid service. Scope and fees are agreed before any paid work begins.
                </p>
              </div>
            ) : (
              <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-testid="contact-form">
                <div>
                  <label htmlFor="c_first" className={labelCls}>First name</label>
                  <input id="c_first" required value={form.first_name} onChange={set("first_name")} data-testid="contact-first-name" className={inputCls} />
                </div>
                <div>
                  <label htmlFor="c_last" className={labelCls}>Last name</label>
                  <input id="c_last" required value={form.last_name} onChange={set("last_name")} data-testid="contact-last-name" className={inputCls} />
                </div>
                <div>
                  <label htmlFor="c_email" className={labelCls}>Email</label>
                  <input id="c_email" type="email" required value={form.email} onChange={set("email")} data-testid="contact-email" className={inputCls} />
                </div>
                <div>
                  <label htmlFor="c_mobile" className={labelCls}>Mobile <span className="text-charcoal/35 normal-case tracking-normal">— optional</span></label>
                  <input id="c_mobile" type="tel" value={form.mobile} onChange={set("mobile")} data-testid="contact-mobile" className={inputCls} placeholder="04xx xxx xxx" />
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="c_msg" className={labelCls}>What are you trying to decide?</label>
                  <textarea id="c_msg" rows={6} required minLength={10} value={form.message} onChange={set("message")} data-testid="contact-message" className={inputCls} placeholder="Tell us the decision you’re weighing up — the more context, the sharper our answer." />
                </div>
                <div className="sm:col-span-2">
                  <button
                    type="submit"
                    disabled={sending}
                    data-testid="contact-submit"
                    className="px-10 py-4 bg-charcoal text-ivory text-xs font-semibold tracking-[0.2em] uppercase transition-[background-color,color] duration-300 hover:bg-gold hover:text-ink disabled:opacity-60"
                  >
                    {sending ? "Sending…" : "Send — and point me in the right direction"}
                  </button>
                  <p className="mt-4 text-xs text-charcoal/50 leading-relaxed">
                    Your details are handled as described in our{" "}
                    <Link to="/privacy-policy" data-testid="contact-privacy-link" className="underline underline-offset-4 hover:text-golddark transition-colors duration-300">
                      Privacy Policy
                    </Link>
                    .
                  </p>
                </div>
              </form>
            )}
          </div>
        </div>
      </section>
    </>
  );
}
